import React from 'react';
import { Breadcrumb as AntBreadcrumb, Tooltip } from 'antd';
import type { BreadcrumbProps as AntdBreadcrumbProps } from 'antd';

type ItemType = NonNullable<AntdBreadcrumbProps['items']>[number];
import { Link } from 'react-router-dom';
import MergeClassNames from '../../../utils/MergeClassNames';

export interface BreadcrumbRoute {
  path: string;
  breadcrumbName: string;
  isActive?: boolean;
  clickable?: boolean;
  children?: BreadcrumbRoute[];
  tooltip?: React.ReactNode;
  icon?: React.ReactNode;
}

export interface BreadcrumbProps {
  routes?: BreadcrumbRoute[];
  className?: string;
  separator?: React.ReactNode;
}

const wrapWithTooltip = (
  node: React.ReactNode,
  tooltip?: React.ReactNode
): React.ReactNode => {
  if (!tooltip) return node;
  return (
    <Tooltip title={tooltip} styles={{ root: { maxWidth: 'none' } }}>
      {node}
    </Tooltip>
  );
};

const Breadcrumb: React.FC<BreadcrumbProps> = ({
  routes,
  className = '',
  separator = '>',
}) => {
  const breadcrumbItems: ItemType[] =
    routes?.map((route) => {
      const {
        isActive = false,
        clickable = true,
        breadcrumbName,
        path,
        tooltip,
        icon,
      } = route;

      const label = icon ? (
        <span className="group inline-flex items-center gap-1">
          <span className="transition-transform group-hover:scale-150">
            {icon}
          </span>
          {breadcrumbName}
        </span>
      ) : (
        breadcrumbName
      );

      if (!clickable) {
        return {
          title: wrapWithTooltip(
            <span className="cursor-default">{label}</span>,
            tooltip
          ),
        };
      } else if (isActive) {
        return {
          title: wrapWithTooltip(
            <span className="underline">{label}</span>,
            tooltip
          ),
        };
      } else {
        return {
          title: wrapWithTooltip(
            <Link className="no-underline hover:underline" to={path}>
              {label}
            </Link>,
            tooltip
          ),
        };
      }
    }) || [];

  return (
    <div className={MergeClassNames('mb-4', className)}>
      <AntBreadcrumb separator={separator} items={breadcrumbItems} />
    </div>
  );
};

export default Breadcrumb;
